import React, { useState, useEffect } from 'react';
import { Calendar, Sparkles, ChevronRight, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCategories } from '@/hooks/useCategories';

interface SearchTypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}

type ListingType = 'all' | 'events' | 'experiences';

export const SearchTypeSelector: React.FC<SearchTypeSelectorProps> = ({
  value,
  onChange,
  onClose
}) => {
  const [selectedType, setSelectedType] = useState<ListingType>(
    value === 'events' ? 'events' : value === 'experiences' ? 'experiences' : 'all'
  );
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  const { eventCategories, experienceCategories, loading } = useCategories();

  const handleTypeSelect = (type: ListingType) => {
    setSelectedType(type);
    setSelectedCategory(null);
    
    if (type === 'all') {
      onChange('all');
      onClose();
    }
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    // Pass both type and category
    onChange(`${selectedType}:${category}`);
    onClose();
  };

  const handleApply = () => {
    if (selectedCategory) {
      onChange(`${selectedType}:${selectedCategory}`);
    } else {
      onChange(selectedType);
    }
    onClose();
  };

  const categories = selectedType === 'events' 
    ? eventCategories 
    : selectedType === 'experiences' 
      ? experienceCategories 
      : [];

  return (
    <div className="p-4">
      <div className="flex gap-6">
        {/* Left Column - Listing Types */}
        <div className="w-1/2 border-r border-border pr-6">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
            Listing Type
          </div>
          
          <div className="space-y-2">
            {/* All Types */}
            <button
              onClick={() => handleTypeSelect('all')}
              className={cn(
                "flex items-center justify-between w-full p-4 rounded-xl transition-colors",
                selectedType === 'all' ? "bg-primary/10 border-2 border-primary" : "hover:bg-muted border-2 border-transparent"
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center",
                  selectedType === 'all' ? "bg-primary text-white" : "bg-muted"
                )}>
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <p className="font-medium">All Types</p>
                  <p className="text-sm text-muted-foreground">Events & Experiences</p>
                </div>
              </div>
              {selectedType === 'all' && <Check className="w-5 h-5 text-primary" />}
            </button>

            {/* Events */}
            <button
              onClick={() => handleTypeSelect('events')}
              className={cn(
                "flex items-center justify-between w-full p-4 rounded-xl transition-colors",
                selectedType === 'events' ? "bg-primary/10 border-2 border-primary" : "hover:bg-muted border-2 border-transparent"
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center",
                  selectedType === 'events' ? "bg-primary text-white" : "bg-muted"
                )}>
                  <Calendar className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Events</p>
                  <p className="text-sm text-muted-foreground">Car meets, races & more</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedType === 'events' && <Check className="w-5 h-5 text-primary" />}
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </button>

            {/* Experiences */}
            <button
              onClick={() => handleTypeSelect('experiences')}
              className={cn(
                "flex items-center justify-between w-full p-4 rounded-xl transition-colors",
                selectedType === 'experiences' ? "bg-primary/10 border-2 border-primary" : "hover:bg-muted border-2 border-transparent"
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center",
                  selectedType === 'experiences' ? "bg-primary text-white" : "bg-muted"
                )}>
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Experiences</p>
                  <p className="text-sm text-muted-foreground">Go-karting, simulators & more</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedType === 'experiences' && <Check className="w-5 h-5 text-primary" />}
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </button>
          </div>
        </div>

        {/* Right Column - Categories */}
        <div className="w-1/2 pl-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
            {selectedType === 'all' ? 'Select a type first' : `${selectedType === 'events' ? 'Event' : 'Experience'} Categories`}
          </div>
          
          {selectedType === 'all' ? (
            <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
              Select Events or Experiences to see categories
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {/* All Categories Option */}
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  onChange(selectedType);
                  onClose();
                }}
                className={cn(
                  "flex items-center justify-between w-full px-4 py-3 rounded-lg transition-colors",
                  !selectedCategory ? "bg-muted font-medium" : "hover:bg-muted"
                )}
              >
                <span>All {selectedType === 'events' ? 'Events' : 'Experiences'}</span>
                {!selectedCategory && <Check className="w-4 h-4 text-primary" />}
              </button>
              
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategorySelect(category)}
                  className={cn(
                    "flex items-center justify-between w-full px-4 py-3 rounded-lg transition-colors",
                    selectedCategory === category ? "bg-muted font-medium" : "hover:bg-muted"
                  )}
                >
                  <span>{category}</span>
                  {selectedCategory === category && <Check className="w-4 h-4 text-primary" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
